import { useState } from "react";
import { toast } from "sonner";
import { Mail, Clock, MessageSquare } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import { api, track, submitLead } from "../../lib/api";

export default function Contact() {
  usePageMeta("Contact Sales — Sellvago", "Talk to Sellvago's sales team about agent access, white-label portals, affiliate partnerships or API integration.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", interest: "Agent / Agency access", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSending(true);
    try {
      await submitLead({
        lead_type: "b2b_contact",
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        message: `[${form.interest}] ${form.message}`,
        source_page: "/contact",
      });
      track("lead_capture", "/contact", { interest: form.interest });
      setSent(true);
      toast.success("Thanks! Our sales team will reach out within one business day.");
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const cls = "w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#970C10] transition-colors duration-200";

  return (
    <div className="pt-32 pb-16 px-6 lg:px-10" data-testid="contact-page">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-4">Contact Sales</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-white">
            Tell us what you're building.
          </h1>
          <p className="mt-5 text-slate-400 leading-relaxed max-w-md">
            Agent access, a white-label portal, affiliate partnership or an API integration — describe your goal and
            we'll map the fastest path to launch.
          </p>
          <div className="mt-10 space-y-5">
            {[
              { icon: Mail, t: "partners@sellvago.com" },
              { icon: Clock, t: "Replies within 1 business day" },
              { icon: MessageSquare, t: "Demo calls available Mon–Sat" },
            ].map((x) => (
              <div key={x.t} className="flex items-center gap-3 text-sm text-slate-300">
                <span className="w-9 h-9 rounded-sm glass-dark flex items-center justify-center">
                  <x.icon className="w-4 h-4 text-[#E8767A]" />
                </span>
                {x.t}
              </div>
            ))}
          </div>
        </div>
        <div className="glass-dark rounded-sm p-8">
          {sent ? (
            <div className="py-16 text-center" data-testid="contact-success">
              <h2 className="font-heading text-2xl font-bold text-white">Request received.</h2>
              <p className="mt-3 text-slate-400 text-sm">We'll be in touch shortly at {form.email}.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name-input" className={cls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                <input data-testid="contact-email-input" className={cls} type="email" placeholder="Work email *" value={form.email} onChange={set("email")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-phone-input" className={cls} placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} />
                <input data-testid="contact-company-input" className={cls} placeholder="Company / brand" value={form.company} onChange={set("company")} />
              </div>
              <select data-testid="contact-interest-select" className={cls} value={form.interest} onChange={set("interest")}>
                {["Agent / Agency access", "White-label portal", "Affiliate program", "API integration", "Something else"].map((o) => (
                  <option key={o} value={o} className="bg-[#032347]">{o}</option>
                ))}
              </select>
              <textarea data-testid="contact-message-input" className={`${cls} h-28 resize-none`} placeholder="Tell us about your business and goals" value={form.message} onChange={set("message")} />
              <button
                data-testid="contact-submit-btn"
                disabled={sending}
                className="w-full bg-[#970C10] hover:bg-[#7A0A0D] text-white font-bold py-3.5 rounded-sm transition-colors duration-200 disabled:opacity-50"
              >
                {sending ? "Sending..." : "Send to Sales Team"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
